import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Activity,
  Download,
  Layers,
  Mic,
  MonitorPlay,
  Music,
  Pause,
  Play,
  Scissors,
  SkipBack,
  SkipForward,
  Video,
  Volume2
} from "lucide-react";
import { t } from "../i18n";
import Sidebar from "../components/Sidebar.jsx";

/**
 * ARCHITECTURE V2 : CONCEPT
 * 1. Store (React) : Detient l'etat de verite (ContentosEditSpec JSON).
 * 2. Engine (Class) : "PlaybackEngine" decouple de l'UI.
 * - Scheduler : Gere la boucle requestAnimationFrame et l'horloge.
 * - Graph Audio : WebAudio API pour le mixage precis.
 * - Compositor : Pipeline de rendu (ici Canvas 2D pour la demo, mais structure pour WebGL).
 * 3. UI (React) : Composants "dumb" qui refletent l'etat de l'Engine.
 */

const buildInitialProject = () => ({
  schema: "contentos.editSpec.v2",
  meta: {
    title: t("editor.project.title"),
    version: 1
  },
  settings: {
    canvas: { width: 1920, height: 1080, fps: 30 },
    audio: { sampleRate: 48000, masterGain: 0.8 }
  },
  timeline: {
    durationMs: 12000,
    tracks: [
      {
        id: "trk_v1",
        type: "video",
        name: t("editor.track.video"),
        items: [
          {
            id: "clip_1",
            startMs: 0,
            durationMs: 4000,
            type: "video_generator",
            variant: "noise",
            name: t("editor.clip.noise"),
            transform: { scale: 1, x: 960, y: 540 }
          },
          {
            id: "clip_2",
            startMs: 4000,
            durationMs: 4000,
            type: "video_generator",
            variant: "plasma",
            name: t("editor.clip.plasma"),
            transform: { scale: 1, x: 960, y: 540 }
          },
          {
            id: "clip_3",
            startMs: 8000,
            durationMs: 4000,
            type: "video_generator",
            variant: "checker",
            name: t("editor.clip.checker"),
            transform: { scale: 1, x: 960, y: 540 }
          }
        ]
      },
      {
        id: "trk_ovl",
        type: "overlay",
        name: t("editor.track.overlay"),
        items: [
          {
            id: "ovl_1",
            startMs: 2000,
            durationMs: 2000,
            type: "shape",
            variant: "circle",
            color: "rgba(255,0,0,0.5)",
            transform: { scale: 1, x: 1600, y: 200 }
          },
          {
            id: "txt_1",
            startMs: 4500,
            durationMs: 3000,
            type: "text",
            text: t("editor.overlay.text"),
            style: { fontSize: 120, color: "#fff" },
            transform: { x: 960, y: 900 }
          }
        ]
      },
      {
        id: "trk_aud",
        type: "audio",
        name: t("editor.track.audio"),
        items: [
          { id: "aud_1", startMs: 0, durationMs: 4000, type: "oscillator", freq: 220, gain: 0.1 },
          { id: "aud_2", startMs: 4000, durationMs: 4000, type: "oscillator", freq: 440, gain: 0.1 },
          { id: "aud_3", startMs: 8000, durationMs: 4000, type: "oscillator", freq: 110, gain: 0.3 }
        ]
      }
    ]
  }
});

class PlaybackEngine {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d", { alpha: false });
    this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();

    this.state = {
      currentTimeMs: 0,
      isPlaying: false,
      project: null
    };

    this.activeOscillators = new Map();
    this.rafId = null;
    this.lastTime = 0;

    this.onTimeUpdate = () => {};
    this.onPlayStateChange = () => {};
  }

  loadProject(project) {
    this.state.project = project;
    this.render(this.state.currentTimeMs);
  }

  play() {
    if (this.state.isPlaying) return;
    if (this.audioCtx.state === "suspended") {
      this.audioCtx.resume();
    }

    this.state.isPlaying = true;
    this.lastTime = performance.now();
    this.loop();
    this.onPlayStateChange(true);
  }

  pause() {
    this.state.isPlaying = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.stopAllAudio();
    this.onPlayStateChange(false);
  }

  seek(timeMs) {
    const wasPlaying = this.state.isPlaying;
    if (wasPlaying) this.pause();

    timeMs = Math.max(0, Math.min(timeMs, this.state.project.timeline.durationMs));
    this.state.currentTimeMs = timeMs;

    this.render(timeMs);
    this.onTimeUpdate(timeMs);

    if (wasPlaying) this.play();
  }

  loop() {
    if (!this.state.isPlaying) return;

    const now = performance.now();
    const delta = now - this.lastTime;
    this.lastTime = now;

    this.state.currentTimeMs += delta;

    if (this.state.currentTimeMs >= this.state.project.timeline.durationMs) {
      this.state.currentTimeMs = 0;
    }

    this.processAudio(this.state.currentTimeMs);
    this.render(this.state.currentTimeMs);
    this.onTimeUpdate(this.state.currentTimeMs);

    this.rafId = requestAnimationFrame(() => this.loop());
  }

  processAudio(timeMs) {
    const { tracks } = this.state.project.timeline;
    const activeIds = new Set();

    tracks
      .filter((track) => track.type === "audio")
      .forEach((track) => {
        track.items.forEach((item) => {
          const isActive =
            timeMs >= item.startMs && timeMs < item.startMs + item.durationMs;

          if (isActive) {
            activeIds.add(item.id);
            if (!this.activeOscillators.has(item.id)) {
              const osc = this.audioCtx.createOscillator();
              const gain = this.audioCtx.createGain();

              osc.type = "sine";
              osc.frequency.setValueAtTime(item.freq || 440, this.audioCtx.currentTime);

              gain.gain.setValueAtTime(0, this.audioCtx.currentTime);
              gain.gain.linearRampToValueAtTime(
                item.gain || 0.1,
                this.audioCtx.currentTime + 0.1
              );

              osc.connect(gain);
              gain.connect(this.audioCtx.destination);
              osc.start();

              this.activeOscillators.set(item.id, { osc, gain });
            }
          }
        });
      });

    this.activeOscillators.forEach((nodes, id) => {
      if (!activeIds.has(id)) {
        nodes.gain.gain.setTargetAtTime(0, this.audioCtx.currentTime, 0.05);
        setTimeout(() => {
          try {
            nodes.osc.stop();
          } catch (err) {
            // ignore
          }
        }, 100);
        this.activeOscillators.delete(id);
      }
    });
  }

  stopAllAudio() {
    this.activeOscillators.forEach((nodes) => {
      try {
        nodes.osc.stop();
      } catch (err) {
        // ignore
      }
    });
    this.activeOscillators.clear();
  }

  render(timeMs) {
    if (!this.ctx || !this.state.project) return;
    const { width, height } = this.state.project.settings.canvas;

    this.ctx.fillStyle = "#000";
    this.ctx.fillRect(0, 0, width, height);

    this.state.project.timeline.tracks.forEach((track) => {
      if (track.type === "audio") return;

      track.items.forEach((item) => {
        if (timeMs >= item.startMs && timeMs < item.startMs + item.durationMs) {
          this.drawItem(item, timeMs, width, height);
        }
      });
    });
  }

  drawItem(item, timeMs, canvasW, canvasH) {
    const localTime = timeMs - item.startMs;
    const ctx = this.ctx;

    ctx.save();

    const { x, y, scale } = item.transform || { x: canvasW / 2, y: canvasH / 2, scale: 1 };
    ctx.translate(x, y);
    ctx.scale(scale, scale);

    if (item.type === "video_generator") {
      if (item.variant === "noise") {
        for (let i = 0; i < 20; i += 1) {
          ctx.fillStyle = `rgba(${Math.random() * 255},${Math.random() * 255},${Math.random() * 255}, 0.8)`;
          ctx.fillRect(
            (Math.random() - 0.5) * 1920,
            (Math.random() - 0.5) * 1080,
            Math.random() * 200,
            Math.random() * 50
          );
        }
        ctx.fillStyle = "#fff";
        ctx.font = "60px monospace";
        ctx.fillText(`FRAME: ${Math.floor(localTime / 33)}`, -100, 0);
      } else if (item.variant === "plasma") {
        const hue = (localTime / 20) % 360;
        const grd = ctx.createRadialGradient(0, 0, 10, 0, 0, 1000);
        grd.addColorStop(0, `hsl(${hue}, 100%, 50%)`);
        grd.addColorStop(1, `hsl(${(hue + 180) % 360}, 100%, 20%)`);
        ctx.fillStyle = grd;
        ctx.fillRect(-960, -540, 1920, 1080);
      } else if (item.variant === "checker") {
        const size = 100;
        const offset = (localTime / 5) % size;
        ctx.fillStyle = "#333";
        ctx.fillRect(-960, -540, 1920, 1080);
        ctx.fillStyle = "#666";
        for (let cx = -960; cx < 960; cx += size) {
          for (let cy = -540; cy < 540; cy += size) {
            if (((cx + cy) / size) % 2 === 0) {
              ctx.fillRect(cx + offset, cy, size / 2, size / 2);
            }
          }
        }
      }
    } else if (item.type === "shape") {
      ctx.fillStyle = item.color;
      ctx.beginPath();
      ctx.arc(0, 0, 100 + Math.sin(localTime / 200) * 20, 0, Math.PI * 2);
      ctx.fill();
    } else if (item.type === "text") {
      ctx.fillStyle = item.style.color;
      ctx.font = `bold ${item.style.fontSize}px Inter, sans-serif`;
      ctx.textAlign = "center";
      ctx.shadowColor = "rgba(0,0,0,0.5)";
      ctx.shadowBlur = 10;
      ctx.fillText(item.text, 0, 0);
    }

    ctx.restore();
  }
}

const TimelineTrack = ({ track, durationMs, currentTimeMs, onClipClick }) => {
  return (
    <div className="flex h-24 bg-neutral-900 border-b border-neutral-800 group">
      <div className="w-48 shrink-0 bg-neutral-800/50 border-r border-neutral-700 p-3 flex flex-col justify-center">
        <div className="flex items-center gap-2 font-medium text-neutral-300 text-sm mb-1">
          {track.type === "video" ? (
            <Video size={14} />
          ) : track.type === "audio" ? (
            <Music size={14} />
          ) : (
            <Layers size={14} />
          )}
          <span className="truncate">{track.name}</span>
        </div>
        <div className="flex gap-2 mt-1">
          <button className="p-1 hover:bg-neutral-700 rounded">
            <Volume2 size={12} className="text-neutral-400" />
          </button>
          <button className="p-1 hover:bg-neutral-700 rounded">
            <Activity size={12} className="text-neutral-400" />
          </button>
        </div>
      </div>

      <div className="flex-1 relative overflow-hidden bg-neutral-900/50">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: "linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "100px 100%"
          }}
        />

        {track.items.map((item) => {
          const startPct = (item.startMs / durationMs) * 100;
          const widthPct = (item.durationMs / durationMs) * 100;
          const isActive =
            currentTimeMs >= item.startMs && currentTimeMs < item.startMs + item.durationMs;

          return (
            <div
              key={item.id}
              onClick={() => onClipClick(item)}
              className={`absolute top-1 bottom-1 rounded-md border overflow-hidden cursor-pointer transition-colors
                ${isActive ? "border-indigo-400 ring-1 ring-indigo-500/50" : "border-neutral-700"}
                ${track.type === "audio" ? "bg-emerald-900/40" : "bg-indigo-900/40"}
              `}
              style={{ left: `${startPct}%`, width: `${widthPct}%` }}
            >
              <div className={`h-full w-full p-2 flex flex-col justify-center ${isActive ? "bg-white/5" : ""}`}>
                <span className="text-xs font-semibold text-white/90 truncate drop-shadow">
                  {item.name || item.id}
                </span>
                <span className="text-[10px] text-white/50 font-mono">
                  {(item.durationMs / 1000).toFixed(1)}s
                </span>
              </div>

              <div className="absolute left-0 top-0 bottom-0 w-3 hover:bg-white/20 cursor-w-resize" />
              <div className="absolute right-0 top-0 bottom-0 w-3 hover:bg-white/20 cursor-e-resize" />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function ContentosEditorV2() {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const [project] = useState(buildInitialProject);

  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeClip, setActiveClip] = useState(null);

  useLayoutEffect(() => {
    if (canvasRef.current && !engineRef.current) {
      const engine = new PlaybackEngine(canvasRef.current);
      engine.onTimeUpdate = (timeMs) => setCurrentTime(timeMs);
      engine.onPlayStateChange = (playing) => setIsPlaying(playing);
      engine.loadProject(project);
      engineRef.current = engine;
    }

    return () => {
      if (engineRef.current) engineRef.current.pause();
    };
  }, [project]);

  useEffect(() => {
    return () => {
      if (engineRef.current) engineRef.current.pause();
    };
  }, []);

  const togglePlay = () => {
    if (!engineRef.current) return;
    if (isPlaying) engineRef.current.pause();
    else engineRef.current.play();
  };

  const handleSeek = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const seekTime = (x / rect.width) * project.timeline.durationMs;
    engineRef.current.seek(seekTime);
  };

  return (
    <div className="app-shell">
      <Sidebar active="editor" />
      <div className="app-main app-main-full">
        <div className="flex flex-col h-screen bg-neutral-950 text-gray-200 font-sans">
          <header className="h-14 bg-neutral-900 border-b border-neutral-800 flex items-center justify-between px-4 shrink-0 z-20">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-600 rounded flex items-center justify-center font-bold text-white shadow-lg shadow-indigo-500/20">
            V2
          </div>
          <div>
            <h1 className="text-sm font-bold text-white leading-tight">{t("editor.header.title")}</h1>
            <div className="text-[10px] text-indigo-400 font-mono">{t("editor.header.subtitle")}</div>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="flex items-center gap-2 bg-black/40 px-3 py-1.5 rounded border border-white/5">
            <MonitorPlay size={14} className="text-neutral-400" />
            <span className="text-xs font-mono text-green-400">{t("editor.header.fps")}</span>
          </div>
          <div className="flex items-center gap-2 bg-black/40 px-3 py-1.5 rounded border border-white/5">
            <Activity size={14} className="text-neutral-400" />
            <span className="text-xs font-mono text-blue-400">{t("editor.header.ram")}</span>
          </div>
        </div>
        <button className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-2">
          <Download size={14} /> {t("editor.header.export")}
        </button>
          </header>

          <div className="flex flex-1 overflow-hidden">
        <div className="w-64 bg-neutral-900 border-r border-neutral-800 p-4 shrink-0 flex flex-col gap-6 overflow-y-auto">
          <div className="text-xs font-bold text-neutral-500 uppercase tracking-wider">{t("editor.inspector.title")}</div>

          {activeClip ? (
            <div className="space-y-4 animate-in fade-in slide-in-from-left-4 duration-300">
              <div className="space-y-1">
                <label className="text-[10px] text-neutral-400">{t("editor.inspector.clipName")}</label>
                <input
                  type="text"
                  value={activeClip.name}
                  readOnly
                  className="w-full bg-neutral-800 border border-neutral-700 rounded px-2 py-1.5 text-xs focus:ring-1 ring-indigo-500 outline-none"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-neutral-400">{t("editor.inspector.transform")}</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={activeClip.transform?.x || 0}
                    readOnly
                    className="w-1/2 bg-neutral-800 border border-neutral-700 rounded px-2 py-1.5 text-xs"
                  />
                  <input
                    type="number"
                    value={activeClip.transform?.y || 0}
                    readOnly
                    className="w-1/2 bg-neutral-800 border border-neutral-700 rounded px-2 py-1.5 text-xs"
                  />
                </div>
              </div>
              {activeClip.type === "video_generator" && (
                <div className="p-3 bg-indigo-900/20 border border-indigo-500/30 rounded">
                  <div className="text-[10px] text-indigo-300 mb-2 font-bold">{t("editor.inspector.procGen")}</div>
                  <div className="text-xs text-indigo-200">
                    {t("editor.inspector.procGenDesc")}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-neutral-600 italic text-center mt-10">
              {t("editor.inspector.empty")}
            </div>
          )}
        </div>

        <div className="flex-1 bg-neutral-950 flex flex-col">
          <div className="flex-1 flex items-center justify-center p-6 relative overflow-hidden">
            <div
              className="relative shadow-2xl shadow-black ring-1 ring-white/10"
              style={{ aspectRatio: "16/9", height: "100%", maxHeight: "80vh" }}
            >
              <canvas ref={canvasRef} width={1920} height={1080} className="w-full h-full bg-black block" />

              <div className="absolute top-4 right-4 font-mono text-xl font-bold text-white drop-shadow-md tabular-nums tracking-widest bg-black/50 px-2 rounded">
                {(currentTime / 1000).toFixed(2)}s
              </div>
            </div>
          </div>

          <div className="h-16 border-t border-neutral-800 bg-neutral-900 flex items-center justify-center gap-6 shrink-0">
            <button className="text-neutral-400 hover:text-white transition-colors">
              <SkipBack size={20} />
            </button>
            <button
              onClick={togglePlay}
              className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-black hover:scale-105 active:scale-95 transition-all"
            >
              {isPlaying ? <Pause size={24} fill="black" /> : <Play size={24} fill="black" className="ml-1" />}
            </button>
            <button className="text-neutral-400 hover:text-white transition-colors">
              <SkipForward size={20} />
            </button>
          </div>
        </div>
          </div>

          <div className="h-80 bg-neutral-950 border-t border-neutral-800 flex flex-col shrink-0">
          <div className="h-9 bg-neutral-900 border-b border-neutral-800 flex items-center justify-between px-2">
            <div className="flex gap-1">
            <button className="p-1.5 hover:bg-neutral-800 rounded text-neutral-400">
              <Scissors size={14} />
            </button>
            <button className="p-1.5 hover:bg-neutral-800 rounded text-neutral-400">
              <Mic size={14} />
            </button>
            </div>
          <div className="text-[10px] text-neutral-500">{t("editor.timeline.duration", "Duration")}: 12.0s</div>
          </div>

        <div className="flex-1 overflow-auto relative custom-scrollbar flex flex-col">
          <div className="relative min-w-full min-h-full">
            <div className="h-8 bg-neutral-900 border-b border-neutral-800 sticky top-0 z-10 flex select-none">
              <div className="w-48 shrink-0 bg-neutral-900 border-r border-neutral-700" />

              <div className="flex-1 relative flex" onMouseDown={handleSeek} style={{ cursor: "crosshair" }}>
                {Array.from({ length: 13 }).map((_, i) => (
                  <div
                    key={i}
                    style={{ width: `${(1000 / project.timeline.durationMs) * 100}%` }}
                    className="h-full border-l border-neutral-700 pl-1 text-[10px] text-neutral-500 pt-1"
                  >
                    {i}s
                  </div>
                ))}

                <div
                  className="absolute top-0 bottom-0 w-px bg-indigo-500 pointer-events-none z-20 h-[1000px]"
                  style={{ left: `${(currentTime / project.timeline.durationMs) * 100}%` }}
                >
                  <div className="w-3 h-3 bg-indigo-500 rotate-45 -translate-x-1.5 -translate-y-1.5 absolute top-0 shadow-sm border border-white" />
                </div>
              </div>
            </div>

            <div className="pb-10">
              {project.timeline.tracks.map((track) => (
                <TimelineTrack
                  key={track.id}
                  track={track}
                  durationMs={project.timeline.durationMs}
                  currentTimeMs={currentTime}
                  onClipClick={setActiveClip}
                />
              ))}
            </div>
          </div>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
}
